import { Alert, Button, Dialog, TextInput, Checkbox } from '@atoms'
import { RadioGroup, RadioGroupItem } from '@atoms/RadioGroup'

import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm, useWatch } from 'react-hook-form'
import { useState } from 'react'

import { useErrorsStore } from '@stores/errorsStore'
import AppErrorsAlert from '@components/molecules/Alerts/AppErrorsAlert'

import useMutation from '@hooks/useGQLMutation'
import { exportRosterMutation } from './mutations'

import type { Event as EventType } from '../../pages/ShiftsOverview'

import { scopedTranslation } from '@frontend/utils/I18n'
const t = scopedTranslation('components.roster_export_modal')

const schema = yup.object({
  email: yup
    .string()
    .email(t('validation.email'))
    .required(t('validation.required')),
  confirmExternal: yup.boolean(),
  format: yup.mixed<'pdf' | 'csv'>().oneOf(['pdf', 'csv']).required(),
}).required()

type ExportFormat = 'pdf' | 'csv'

type FormData = {
  email: string
  confirmExternal?: boolean
  format: ExportFormat
}

type RootProps = {
  open: boolean
  setOpen: (open: boolean) => void
  event: EventType
}

export default function RosterExportModal({ open, setOpen, event }: RootProps) {
  const [showConfirmation, setShowConfirmation] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)

  const { 
    setErrors: setAppErrors, 
    clearErrors: clearAppErrors,
  } = useErrorsStore(({ setErrors, clearErrors }) => ({
    setErrors,
    clearErrors,
  }))

  const { 
    register, 
    handleSubmit, 
    formState: { errors }, 
    control, 
    setError, 
    reset,
    setValue,
    watch
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues: { confirmExternal: false, format: 'pdf' },
  })

  const emailValue = useWatch({ name: 'email', control })
  const exportFormat = watch('format')

  const exportRosterMutationHook = useMutation(exportRosterMutation)

  const handleExportSuccess = (result: any) => {
    clearAppErrors('exportRoster')
  
    if (result?.exportRoster.needsConfirmation) {
      setShowConfirmation(true)
    } else {
      setShowConfirmation(false)
      setShowSuccess(true)
      reset()
    }
  }
  
  const handleExportError = () => {
    clearAppErrors('exportRoster')
    setAppErrors({
      errorsPath: 'exportRoster',
      errors: [
        {
          code: 'SEND_ROSTER_EXPORT_ERROR',
          message: t('errors.export_failed'),
        },
      ],
    })
  }

  const onSubmit = (data: FormData) => {
    if (showConfirmation && !data.confirmExternal) {
      setError('confirmExternal', { type: 'manual', message: t('validation.confirm') })
      return
    }

    const input = {
      recipientEmail: data.email,
      id: event.id,
      confirmExternalRecipient: data?.confirmExternal ?? false,
      format: data.format
    }

    setShowSuccess(false)

    exportRosterMutationHook.mutate({ input }, {
      onSuccess: handleExportSuccess,
      onError: handleExportError,
    })
  }

  const renderConfirmationAlert = () =>
    showConfirmation && (
      <Alert type="warning" title={t('alerts.confirmation.title')}>
        <p className="tw-text-sm">
          {t('alerts.confirmation.body')}
        </p>
      </Alert>
    )
  
  const renderSuccessAlert = () =>
    showSuccess && (
      <Alert type="success" title={t('alerts.success.title')}>
        {t('alerts.success.body')}
      </Alert>
    )

  const resetModalState = () => {
    reset()
    setShowConfirmation(false)
    setShowSuccess(false)
  }

  return (
    <Dialog.Root 
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) resetModalState()
        setOpen(nextOpen)
      }}
    >
      <Dialog.Content size="2xl">
        <Dialog.Header>
          <Dialog.Title>{t('title', { event_name: event.name })}</Dialog.Title>
          <Dialog.Description className="tw-font-medium">
          <div className="tw-pt-3">
            <AppErrorsAlert errorsPath="exportRoster" />
            {renderConfirmationAlert()}
            {renderSuccessAlert()}
          </div>
          </Dialog.Description>
        </Dialog.Header>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='tw-pb-5'>
            <div className="tw-mb-4">
              <label className="tw-block tw-font-medium tw-mb-4 tw-text-gray-700">{t('form.format.label')}</label>
              <RadioGroup
                value={exportFormat}
                onValueChange={value => setValue('format', value as ExportFormat)}
                aria-label={t('form.format.label')}
              >
                <label className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700">
                  <RadioGroupItem value="pdf" />
                  {t('form.format.pdf')}
                </label>
                <label className="tw-flex tw-items-center tw-gap-2 tw-text-gray-700 tw-mb-0">
                  <RadioGroupItem value="csv" />
                  {t('form.format.csv')}
                </label>
              </RadioGroup>
            </div>

            <TextInput 
              displayName={t('form.email_label')}
              {...register('email')}
              errors={errors}
              description={showSuccess ? t('form.prompt_next') : t('form.prompt')}
            />

            {showConfirmation && (
              <div className="tw-flex tw-flex-wrap tw-items-center tw-w-full tw-pt-2">
                <Checkbox
                  name="confirmExternal"
                  control={control}
                  errors={errors}
                >
                  {t('form.confirm_external', { email: emailValue })}
                </Checkbox>
              </div>
            )}
          </div>

          <Dialog.Footer>
            <Dialog.Close asChild>
              <Button type="button" size="xl" rank="tertiary">
                {t('form.close')}
              </Button>
            </Dialog.Close>
            <Button type="submit" size="xl" rank="primary">
              {t('form.submit')}
            </Button>
          </Dialog.Footer>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  )
}
